/**
 * MobileStationOverview.tsx
 *
 * Mobile view showing all stations with their current status.
 * Tap a station card to see session details.
 * Color-coded status badges for quick visual scanning.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useLanguage } from '../../../i18n/LanguageContext';
import stationManagementService, { StationWithDetails } from '../../../services/stationManagementService';
import { wsService } from '../../../services/websocket';
import {
  LayoutGrid,
  Loader2,
  ChevronRight,
  MapPin,
  User,
  Package,
  Filter,
  Settings,
} from 'lucide-react';
import { getStatusBadgeClass, getStatusDotClass } from '../../../utils/statusColors';

const MobileStationOverview: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [showFilter, setShowFilter] = useState(false);

  // Fetch stations
  const { data: stations, isLoading, refetch } = useQuery(
    ['mobile-stations-overview'],
    () => stationManagementService.getStations(),
    { staleTime: 30000 }
  );

  useEffect(() => {
    wsService.connect();

    const invalidateOverview = () => {
      queryClient.invalidateQueries(['mobile-stations-overview']);
    };

    wsService.on('production-session-updated', invalidateOverview);
    wsService.on('station-updated', invalidateOverview);
    wsService.on('worker-clocked-in', invalidateOverview);
    wsService.on('worker-clocked-out', invalidateOverview);
    wsService.on('box-packed', invalidateOverview);
    wsService.on('box-cancelled', invalidateOverview);
    wsService.on('box-deleted', invalidateOverview);
    wsService.on('pending-label-voided', invalidateOverview);

    return () => {
      wsService.off('production-session-updated', invalidateOverview);
      wsService.off('station-updated', invalidateOverview);
      wsService.off('worker-clocked-in', invalidateOverview);
      wsService.off('worker-clocked-out', invalidateOverview);
      wsService.off('box-packed', invalidateOverview);
      wsService.off('box-cancelled', invalidateOverview);
      wsService.off('box-deleted', invalidateOverview);
      wsService.off('pending-label-voided', invalidateOverview);
    };
  }, [queryClient]);

  // Get unique locations
  const locations = React.useMemo(() => {
    if (!stations) return [];
    const uniqueLocations = new Set(
      stations.map((s: StationWithDetails) => s.location_name || s.location_code).filter(Boolean)
    );
    return Array.from(uniqueLocations).sort() as string[];
  }, [stations]);

  // Filter stations by location
  const filteredStations = React.useMemo(() => {
    if (!stations) return [];
    if (!selectedLocation) return stations;
    return stations.filter(
      (s: StationWithDetails) =>
        (s.location_name || s.location_code) === selectedLocation
    );
  }, [stations, selectedLocation]);

  // Group by status for summary
  const statusCounts = React.useMemo(() => {
    if (!filteredStations) return { active: 0, ready: 0, planned: 0, paused: 0, idle: 0 };
    return {
      active: filteredStations.filter((s: StationWithDetails) =>
        s.session_status === 'active' || s.session_status === 'in_progress'
      ).length,
      ready: filteredStations.filter((s: StationWithDetails) =>
        s.session_status === 'ready'
      ).length,
      planned: filteredStations.filter((s: StationWithDetails) =>
        s.session_status === 'received'
      ).length,
      paused: filteredStations.filter((s: StationWithDetails) =>
        s.session_status === 'paused'
      ).length,
      idle: filteredStations.filter((s: StationWithDetails) =>
        !s.session_number && s.enabled !== false
      ).length,
    };
  }, [filteredStations]);

  const getStationDotColor = (station: StationWithDetails) => {
    if (station.enabled === false) return 'bg-gray-400';
    return station.session_status ? getStatusDotClass(station.session_status) : 'bg-gray-400';
  };

  const getStationBadge = (station: StationWithDetails) => {
    if (station.enabled === false) {
      return { text: t('supervisor.disabled') || 'Disabled', className: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400' };
    }
    if (station.session_status && ['active', 'in_progress', 'ready', 'received', 'paused'].includes(station.session_status)) {
      const labelMap: Record<string, string> = {
        active: t('supervisor.active') || 'Active',
        in_progress: t('supervisor.active') || 'Active',
        ready: t('supervisor.ready') || 'Ready',
        planned: t('supervisor.planned') || 'Submitted',
        paused: t('supervisor.paused') || 'Paused',
      };
      return { text: labelMap[station.session_status], className: getStatusBadgeClass(station.session_status) };
    }
    return { text: t('supervisor.idle') || 'Idle', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' };
  };

  const handleStationClick = (station: StationWithDetails) => {
    // Navigate to session details if station has a session
    if (station.session_number) {
      navigate(`/supervisor/sessions?station=${station.station_id}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <button
          onClick={() => navigate('/supervisor')}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <LayoutGrid className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">{t('mobileSupervisor.stationOverview') || 'Station Overview'}</h1>
          <p className="text-sm opacity-80">
            {filteredStations.length} {t('supervisor.stations') || 'stations'}
          </p>
        </div>
        <button
          onClick={() => navigate('/supervisor/station-settings')}
          className="p-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <Settings className="w-5 h-5" />
        </button>
        <button
          onClick={() => setShowFilter(!showFilter)}
          className={`p-2 rounded-lg transition-colors ${showFilter ? 'bg-white/20' : 'hover:bg-white/10'}`}
        >
          <Filter className="w-5 h-5" />
        </button>
        {isLoading && <Loader2 className="w-5 h-5 animate-spin opacity-70" />}
      </header>

      {/* Filter Bar */}
      {showFilter && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">
            {t('supervisor.filterByLocation') || 'Filter by Location'}
          </label>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('supervisor.allLocations') || 'All Locations'}</option>
            {locations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
      )}

      {/* Status Summary Bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex justify-between text-center">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{statusCounts.active}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-cyan-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{statusCounts.ready}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-purple-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{statusCounts.planned}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{statusCounts.paused}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{statusCounts.idle}</span>
          </div>
        </div>
      </div>

      {/* Station List */}
      <main className="flex-1 p-4 space-y-3">
        {isLoading && !stations ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredStations.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('supervisor.noStationsFound') || 'No stations found'}
          </div>
        ) : (
          filteredStations.map((station: StationWithDetails) => {
            const statusBadge = getStationBadge(station);
            const hasSession = !!station.session_number;

            return (
              <button
                key={station.station_id}
                onClick={() => handleStationClick(station)}
                disabled={!hasSession}
                className={`w-full bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-left transition-all ${
                  hasSession ? 'active:scale-98' : 'opacity-70'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Status Indicator */}
                  <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${getStationDotColor(station)}`} />

                  {/* Station Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {station.station_name}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${statusBadge.className}`}>
                        {statusBadge.text}
                      </span>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <MapPin className="w-3 h-3" />
                      <span>{station.location_name || station.location_code || '-'}</span>
                    </div>

                    {/* Session Details (if active) */}
                    {hasSession && (
                      <div className="space-y-1">
                        {/* Product */}
                        <div className="flex items-center gap-1.5 text-sm">
                          <Package className="w-3.5 h-3.5 text-purple-500" />
                          <span className="text-gray-700 dark:text-gray-300 truncate">
                            {station.product_name || station.product_sku || '-'}
                          </span>
                        </div>

                        {/* Operator */}
                        <div className="flex items-center gap-1.5 text-sm">
                          <User className="w-3.5 h-3.5 text-green-500" />
                          <span className="text-gray-700 dark:text-gray-300 truncate">
                            {station.current_operator_name || station.assigned_operator_name || t('supervisor.notAssigned') || 'Not assigned'}
                          </span>
                        </div>

                        {/* Progress Bar */}
                        {station.total_boxes > 0 && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-500">{t('supervisor.progress') || 'Progress'}</span>
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                {station.boxes_produced || 0}/{station.total_boxes} {t('supervisor.boxes') || 'boxes'}
                              </span>
                            </div>
                            <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-purple-500 rounded-full transition-all"
                                style={{ width: `${Math.min(100, ((station.boxes_produced || 0) / station.total_boxes) * 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Arrow (if clickable) */}
                  {hasSession && (
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })
        )}
      </main>
    </div>
  );
};

export default MobileStationOverview;
